export const PAID_SUBSCRIPTION_SUFFIX =
  "Please upgrade to a paid subscription for higher limits and priority processing.";

export const PAID_SUBSCRIPTION_PATH = "/contactpage";

export const FREE_PLAN_MAX_VIDEO_MB = 100;
export const FREE_PLAN_MAX_DURATION_MINUTES = 20;
export const FREE_PLAN_MAX_TOTAL_DURATION_HOURS = 3;
/** API Gateway rejects multipart bodies above ~10 MB before Lambda runs */
export const API_GATEWAY_MAX_UPLOAD_MB = 10;

const PLAN_LIMIT_MARKERS = [
  "too long",
  "20 minute",
  "15 minute",
  "duration limit",
  "maximum supported duration",
  "free plan limit reached",
  "quota",
  "insufficient_quota",
  "3 hour",
  "100 mb",
  "maximum supported size",
  "plan limit",
  "upgrade to a paid subscription",
];

const GATEWAY_PAYLOAD_MARKERS = [
  "request too long",
  "content too large",
  "payload too large",
  "direct api upload",
  "presigned upload",
  "upload request is too large",
];

export const buildPaidSubscriptionMessage = (baseMessage) => {
  const trimmed = String(baseMessage || "").trim();

  if (!trimmed) {
    return PAID_SUBSCRIPTION_SUFFIX;
  }

  if (trimmed.includes(PAID_SUBSCRIPTION_SUFFIX)) {
    return trimmed;
  }

  return `${trimmed} ${PAID_SUBSCRIPTION_SUFFIX}`;
};

export const isGatewayPayloadLimitError = (error, message = "") => {
  const status = error?.response?.status;
  const combined = `${message} ${error?.message || ""} ${JSON.stringify(error?.response?.data || "")}`.toLowerCase();

  if (status === 413 && !combined.includes("100 mb")) {
    return true;
  }

  return GATEWAY_PAYLOAD_MARKERS.some((marker) => combined.includes(marker));
};

export const isCaptionResourceLimitError = (error, message = "") => {
  const status = error?.response?.status;
  const reason = String(error?.response?.data?.reason || "").toLowerCase();
  const combined = `${message} ${error?.message || ""} ${JSON.stringify(error?.response?.data || "")}`.toLowerCase();

  if (status === 502 || status === 503) {
    return false;
  }

  if (status === 402) {
    return true;
  }

  if (status === 413) {
    if (reason === "file_size_limit" || combined.includes("100 mb")) {
      return true;
    }

    return false;
  }

  return PLAN_LIMIT_MARKERS.some((marker) => combined.includes(marker));
};

export const resolveCaptionStatusMessage = (message, error = null) => {
  const resolvedMessage = String(message || "").trim();
  const gatewayPayloadLimit = isGatewayPayloadLimitError(error, resolvedMessage);
  const needsSubscription = !gatewayPayloadLimit && isCaptionResourceLimitError(error, resolvedMessage);

  let displayMessage = resolvedMessage;

  if (gatewayPayloadLimit) {
    displayMessage =
      resolvedMessage ||
      "Upload failed due to a gateway size limit. Please refresh the page and try again.";
  }

  return {
    message: needsSubscription
      ? buildPaidSubscriptionMessage(displayMessage)
      : displayMessage,
    requiresPaidSubscription: needsSubscription,
    consumptionReason: needsSubscription
      ? classifyConsumptionReason(displayMessage, error)
      : "",
    isGatewayPayloadLimit: gatewayPayloadLimit,
  };
};

export const classifyConsumptionReason = (message = "", error = null) => {
  const combined = `${message} ${error?.message || ""}`.toLowerCase();
  const status = error?.response?.status;
  const reason = String(error?.response?.data?.reason || "").toLowerCase();

  if (combined.includes("too large") || combined.includes("100 mb") || reason === "file_size_limit") {
    return "file_size_limit";
  }

  if (
    combined.includes("too long") ||
    combined.includes("duration") ||
    combined.includes("20 minute") ||
    combined.includes("15 minute")
  ) {
    return "resource_limit";
  }

  if (
    combined.includes("quota") ||
    combined.includes("insufficient_quota") ||
    combined.includes("3 hour") ||
    combined.includes("free plan limit reached") ||
    reason === "quota_exceeded"
  ) {
    return "quota_exceeded";
  }

  if (combined.includes("rate limit") || combined.includes("too many requests") || status === 429) {
    return "rate_limit";
  }

  if (combined.includes("timeout") || combined.includes("timed out")) {
    return "processing_timeout";
  }

  return "resource_limit";
};

export const CONSUMPTION_REASON_LABELS = {
  file_size_limit: "File size limit",
  quota_exceeded: "Quota exceeded",
  rate_limit: "Rate limit",
  processing_timeout: "Processing timeout",
  resource_limit: "Resource limit",
};
